import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Badge } from "react-bootstrap";
import axios from "./axiosConfig"; // ← 用你现有的 axios 封装

interface CreateProbeModalProps {
  show: boolean;
  onClose: () => void;
  onCreated: () => void; // 创建成功后刷新列表
}

const isValidHttpUrl = (value: string) => {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const CreateProbeModal: React.FC<CreateProbeModalProps> = ({
  show,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [probeCode, setProbeCode] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [enabled, setEnabled] = useState(true);

  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      // reset when closed
      setName("");
      setProbeCode("");
      setBaseUrl("");
      setEnabled(true);
      setTags([]);
      setTagInput("");
      setError(null);
      setSubmitting(false);
    }
  }, [show]);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagInput("");
  };

  const removeTag = (t: string) => {
    setTags(tags.filter((x) => x !== t));
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!probeCode.trim()) {
      setError("Probe Code is required");
      return;
    }
    if (!isValidHttpUrl(baseUrl)) {
      setError("Base URL must be http or https");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post("/api/probes", {
        name,
        probe_code: probeCode,
        base_url: baseUrl,
        enabled,
        tags: JSON.stringify(tags),
      });

      onClose();
      onCreated();
    } catch (e: any) {
      setError(
        e?.response?.data?.error || "Failed to create probe"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create Probe</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          {/* Name */}
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Frankfurt Probe"
            />
          </Form.Group>

          {/* Probe Code */}
          <Form.Group className="mb-3">
            <Form.Label>Probe Code</Form.Label>
            <Form.Control
              value={probeCode}
              onChange={(e) =>
                setProbeCode(
                  e.target.value.replace(/[^a-zA-Z0-9_-]/g, "")
                )
              }
              placeholder="unique_code"
            />
            <Form.Text muted>
              Unique identifier, cannot be changed later
            </Form.Text>
          </Form.Group>

          {/* Base URL */}
          <Form.Group className="mb-3">
            <Form.Label>Base URL</Form.Label>
            <Form.Control
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="http://127.0.0.1:18090"
              isInvalid={!!baseUrl && !isValidHttpUrl(baseUrl)}
            />
            <Form.Text muted>
              Lynxguard backend must be able to reach this address
            </Form.Text>
          </Form.Group>

          {/* Enabled */}
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              label={enabled ? "Enabled" : "Disabled"}
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </Form.Group>

          {/* Tags */}
          <Form.Group>
            <Form.Label>Tags</Form.Label>
            <div className="mb-2">
              {tags.map((t) => (
                <Badge
                  key={t}
                  bg="secondary"
                  className="me-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => removeTag(t)}
                >
                  {t} ✕
                </Badge>
              ))}
            </div>
            <Form.Control
              value={tagInput}
              placeholder="Press Enter to add tag"
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Creating..." : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateProbeModal;
